// /review — coach review surface tab.
//
// Top: ASK THE COACH on-demand trigger.
// Below: collapsible list of past coach calls, newest first. Opening a
// call reveals narrative + macros card + week-by-week plan + run arc.

import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { apiClient, formatApiError } from '../lib/api';
import { C, FONT_MONO, LABEL_TRACKING } from '../lib/design';
import {
  CoachCallListResponseSchema,
  type CoachCallListResponse,
} from '../lib/types';
import { CoachCallList } from '../components/review/CoachCallList';
import { EmptyReview } from '../components/review/EmptyReview';
import { RunCoachButton } from '../components/review/RunCoachButton';

function nextSundayLabel(now: Date = new Date()): string {
  const dow = now.getDay();
  const daysUntil = dow === 0 ? 7 : 7 - dow;
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysUntil);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`;
}

function ReviewPage() {
  const list = useQuery<CoachCallListResponse>({
    queryKey: ['coach-list'],
    queryFn: () =>
      apiClient.get<CoachCallListResponse>('/api/coach', { schema: CoachCallListResponseSchema }),
    retry: 1,
  });

  const nextSunday = nextSundayLabel();

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <RunCoachButton />

      {list.isLoading && (
        <div style={{ color: C.dim, fontSize: 10, letterSpacing: LABEL_TRACKING, fontFamily: FONT_MONO }}>
          LOADING COACH HISTORY…
        </div>
      )}

      {list.error && (
        <div
          style={{
            padding: 14,
            background: C.panel,
            border: `1px solid ${C.red}`,
            color: C.red,
            fontFamily: FONT_MONO,
            fontSize: 12,
          }}
        >
          {formatApiError(list.error)}
        </div>
      )}

      {list.data && list.data.calls.length === 0 && (
        <EmptyReview nextSundayLabel={nextSunday} noCallYet={true} />
      )}

      {list.data && list.data.calls.length > 0 && (
        <CoachCallList calls={list.data.calls} />
      )}
    </div>
  );
}

export const Route = createFileRoute('/review')({
  component: ReviewPage,
});
